import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import type { ToolbarChildrenProps } from '@draft-js-plugins/inline-toolbar/lib/components/Toolbar';
import ColorPicker from 'admin/components/pages/Story/CardEditor/Inspector/PropField/ColorPicker';
import StorySwatches from 'admin/components/pages/Story/CardEditor/Inspector/PropField/StorySwatches';
import css from './Picker.scss';

export type SwatchesConfig = false | 'admin-story' | 'client-story' | string[];

interface Props extends ToolbarChildrenProps {
	color: string;
	toggleColor: (color: string) => void;
	className?: string;
	swatches: SwatchesConfig;
}

type PickerToolProps = {
	color: string;
	onChange: (color: string) => void;
	onOverrideContent: ToolbarChildrenProps['onOverrideContent'];
	swatches: SwatchesConfig;
};

const PickerTool = (props: PickerToolProps) => {
	const ref = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		function onClickOutside(event: globalThis.MouseEvent) {
			if (!ref.current?.contains(event.target as Node)) props.onOverrideContent(undefined);
		}

		document.addEventListener('click', onClickOutside, { capture: true });

		return () => {
			document.removeEventListener('click', onClickOutside, { capture: true });
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderPicker = (swatches?: string[]) => (
		<ColorPicker
			color={props.color}
			onChange={props.onChange}
			theme={{ root: css.pickerRoot, area: css.pickerArea, inputs: css.pickerInputs }}
			swatches={swatches}
		/>
	);
	return (
		<div className={cn(css.picker, { [css.minimized]: window.innerHeight < 500 })} ref={ref}>
			{props.swatches === 'admin-story' || props.swatches === 'client-story' ? (
				<StorySwatches source={props.swatches}>{renderPicker}</StorySwatches>
			) : (
				renderPicker(props.swatches || undefined)
			)}
		</div>
	);
};

const Picker: React.FC<Props> = ({ color: propColor, toggleColor, className, onOverrideContent, swatches }) => {
	const [defaultColor] = useState('rgba(0, 0, 0, 1)');
	const rootRef = useRef<HTMLDivElement>(null);

	const currentColor = propColor || defaultColor;

	const handleClick = () => {
		const content = () => (
			<PickerTool
				onChange={toggleColor}
				color={currentColor}
				onOverrideContent={onOverrideContent}
				swatches={swatches}
			/>
		);
		onOverrideContent(content);
	};

	return (
		<div ref={rootRef}>
			<button type="button" className={className} onClick={handleClick}>
				<div className={css.color} style={{ background: currentColor }} />
			</button>
		</div>
	);
};

export default Picker;
